import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Loader2, Coins, RotateCcw, Crown, Tag, Gift, Plus, ArrowUpRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Token Activity Ledger — compact timeline of token grants, auto-refunds,
 * upgrades, and discounts. Pulls from GET /api/auth/me/token-activity which
 * normalises the user's `applied_offers` array.
 */
const TokenActivityLedger = () => {
  const { token } = useAuth();
  const auth = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ balance: 0, items: [], total: 0 });
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/auth/me/token-activity`, { headers: auth });
        setData(r.data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Could not load token activity.");
      } finally {
        setLoading(false);
      }
    })();
  }, [auth]);

  if (loading) {
    return (
      <div className="bg-white border border-ink-200 rounded-3xl p-5 flex items-center justify-center" data-testid="token-activity-loading">
        <Loader2 className="w-5 h-5 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="bg-white border border-ink-200 rounded-3xl p-5 space-y-4" data-testid="token-activity-ledger">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-amber-600">
            Token activity
          </p>
          <h2 className="text-xl font-black text-ink-950 tracking-tight">
            Your tokens, your trail.
          </h2>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200">
          <Coins className="w-4 h-4 text-amber-600" />
          <span className="text-sm font-bold text-ink-950" data-testid="token-activity-balance">
            {data.balance.toLocaleString()} tokens
          </span>
        </div>
      </div>

      {error && (
        <p className="text-sm text-rose-500" data-testid="token-activity-error">{error}</p>
      )}

      {!error && data.items.length === 0 && (
        <div className="text-center py-8 text-ink-500" data-testid="token-activity-empty">
          <Coins className="w-8 h-8 text-ink-300 mx-auto mb-2" />
          <p className="text-sm">No token activity yet. Refunds and grants will appear here.</p>
        </div>
      )}

      {data.items.length > 0 && (
        <ul className="divide-y divide-ink-100" data-testid="token-activity-list">
          {data.items.map((it, idx) => (
            <LedgerRow key={idx} item={it} />
          ))}
        </ul>
      )}

      {data.total > data.items.length && (
        <p className="text-[11px] text-ink-500 text-center">
          Showing the latest 100 of {data.total} events.
        </p>
      )}
    </div>
  );
};

const KIND_META = {
  grant:        { Icon: Gift,    color: "text-emerald-600", bg: "bg-emerald-50",  label: "Grant" },
  refund:       { Icon: RotateCcw, color: "text-blue-600",  bg: "bg-blue-50",     label: "Refund" },
  upgrade:      { Icon: Crown,   color: "text-amber-600",   bg: "bg-amber-50",    label: "Upgrade" },
  discount:     { Icon: Tag,     color: "text-violet-600",  bg: "bg-violet-50",   label: "Discount" },
  bonus_videos: { Icon: Plus,    color: "text-fuchsia-600", bg: "bg-fuchsia-50",  label: "Bonus" },
  other:        { Icon: Coins,   color: "text-ink-600",     bg: "bg-ink-50",      label: "Adjustment" },
};

const LedgerRow = ({ item }) => {
  const meta = KIND_META[item.kind] || KIND_META.other;
  const { Icon } = meta;
  const dateLabel = formatDate(item.date);
  const amount = item.amount;

  return (
    <li className="py-3 flex items-start gap-3" data-testid={`token-activity-item-${item.kind}`}>
      <div className={`shrink-0 w-9 h-9 rounded-full ${meta.bg} flex items-center justify-center`}>
        <Icon className={`w-4 h-4 ${meta.color}`} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-bold text-ink-950 truncate">{item.label}</p>
          {amount !== null && amount !== undefined && (
            <span
              className={`text-sm font-black whitespace-nowrap ${amount >= 0 ? "text-emerald-600" : "text-rose-600"}`}
              data-testid="token-activity-amount"
            >
              {amount >= 0 ? "+" : ""}{amount} tokens
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 text-[11px] text-ink-500 mt-0.5">
          <span className="uppercase tracking-[0.14em] font-bold">{meta.label}</span>
          <span>·</span>
          <span>{dateLabel}</span>
          {item.task_id && (
            <>
              <span>·</span>
              <a
                href={`/dashboard/video?task=${item.task_id}`}
                className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                data-testid="token-activity-task-link"
              >
                View task <ArrowUpRight className="w-3 h-3" />
              </a>
            </>
          )}
        </div>
      </div>
    </li>
  );
};

const formatDate = (iso) => {
  if (!iso) return "Unknown date";
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const diffMin = Math.round(diffMs / 60000);
    if (diffMin < 1) return "just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffH = Math.round(diffMin / 60);
    if (diffH < 24) return `${diffH}h ago`;
    const diffD = Math.round(diffH / 24);
    if (diffD < 14) return `${diffD}d ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
};

export default TokenActivityLedger;
